import {
  BillingService,
  Subscription,
  SubscriptionPlan,
  Invoice,
} from "../illing/types";
import { supabase } from "../../lib/supabase";
import { getSupportProduct } from "../supportProduct";

export class LemonSqueezyService implements BillingService {
  private readonly supabase = supabase;
  private readonly baseUrl: string;
  private readonly storeId: string;

  constructor() {
    this.baseUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1`;
    this.storeId = import.meta.env.VITE_LEMONSQUEEZY_STORE_ID;
  }

  /**
   * Récupère l'abonnement actuel depuis Supabase profiles
   */
  async getCurrentSubscription(): Promise<Subscription> {
    try {
      // getSession() est local (pas d'appel réseau) contrairement à getUser()
      const {
        data: { session },
        error: authError,
      } = await this.supabase.auth.getSession();

      if (authError || !session?.user) {
        throw new Error("User not authenticated");
      }

      const user = session.user;

      // Timeout 8s pour éviter la page bloquée indéfiniment si le pool sature
      const profilePromise = this.supabase
        .from("profiles")
        .select("*")
        .eq("id", user.id)
        .single();

      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error("profile fetch timeout")), 8000),
      );

      const { data: profile, error: profileError } = (await Promise.race([
        profilePromise,
        timeoutPromise,
      ])) as Awaited<typeof profilePromise>;

      if (profileError) {
        console.error("Error fetching profile:", profileError);
        return this.createFallbackSubscription();
      }

      const planType = (profile.plan_type || "FREE").toUpperCase();

      return {
        id: profile.lemonsqueezy_subscription_id || "default",
        plan: this.createSubscriptionPlan(planType),
        status: this.mapSubscriptionStatus(
          profile.lemonsqueezy_status || "active",
        ),
        currentPeriodStart: profile.lemonsqueezy_current_period_start
          ? new Date(profile.lemonsqueezy_current_period_start)
          : new Date(),
        currentPeriodEnd: profile.lemonsqueezy_current_period_end
          ? new Date(profile.lemonsqueezy_current_period_end)
          : new Date(Date.now() + 365 * 24 * 60 * 60 * 1000), // 1 an par défaut
        cancelAtPeriodEnd: profile.lemonsqueezy_cancel_at_period_end || false,
      };
    } catch (error) {
      console.error("Error fetching subscription:", error);
      // Fallback vers FREE
      return this.createFallbackSubscription();
    }
  }

  /**
   * Crée un abonnement fallback pour les erreurs
   */
  private createFallbackSubscription(): Subscription {
    return {
      id: "fallback",
      plan: this.createSubscriptionPlan("FREE"),
      status: "active",
      currentPeriodStart: new Date(),
      currentPeriodEnd: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000),
      cancelAtPeriodEnd: false,
    };
  }

  /**
   * Map le statut Lemon Squeezy vers le statut interne
   */
  private mapSubscriptionStatus(lemonStatus: string): Subscription["status"] {
    const statusMap: Record<string, Subscription["status"]> = {
      active: "active",
      cancelled: "canceled",
      past_due: "past_due",
      trialing: "trialing",
      unpaid: "past_due",
      paused: "canceled",
    };

    return statusMap[lemonStatus] || "active";
  }

  /**
   * Récupère les plans disponibles
   */
  async getAvailablePlans(): Promise<SubscriptionPlan[]> {
    return [
      this.createSubscriptionPlan("FREE"),
      this.createSubscriptionPlan("SOLO"),
    ];
  }

  /**
   * Récupère la méthode de paiement actuelle
   */
  async getPaymentMethod(): Promise<string> {
    try {
      // Récupérer l'utilisateur authentifié
      const {
        data: { user },
        error: authError,
      } = await this.supabase.auth.getUser();

      if (authError || !user) {
        throw new Error("User not authenticated");
      }

      // Récupérer le profil pour obtenir les infos de paiement
      const { data: profile, error: profileError } = await this.supabase
        .from("profiles")
        .select("lemonsqueezy_card_brand, lemonsqueezy_card_last4")
        .eq("id", user.id)
        .single();

      if (profileError) {
        console.error("Error fetching payment method:", profileError);
        return "Aucune méthode de paiement";
      }

      if (profile.lemonsqueezy_card_brand && profile.lemonsqueezy_card_last4) {
        return `${profile.lemonsqueezy_card_brand.toUpperCase()} ****${profile.lemonsqueezy_card_last4}`;
      }

      return "Aucune méthode de paiement";
    } catch (error) {
      console.error("Error fetching payment method:", error);
      return "Aucune méthode de paiement";
    }
  }

  /**
   * Récupère la liste des factures via la Edge Function get-invoices
   */
  async getInvoices(): Promise<Invoice[]> {
    // On laisse volontairement remonter les erreurs : l'UI (React Query) a
    // une branche `error` avec bouton Réessayer. Swallower en `[]` masquait
    // les 404 (edge function non déployée), 401 (token), 500 (secret manquant)
    // et faisait croire à l'utilisateur qu'il n'avait pas de factures.
    const {
      data: { session },
      error: authError,
    } = await this.supabase.auth.getSession();

    if (authError || !session) {
      throw new Error("User not authenticated");
    }

    const response = await fetch(`${this.baseUrl}/get-invoices`, {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
      },
    });

    if (!response.ok) {
      let body = "";
      try {
        body = await response.text();
      } catch {
        /* ignore */
      }
      throw new Error(
        `get-invoices ${response.status} ${response.statusText}${body ? ` — ${body.slice(0, 200)}` : ""}`,
      );
    }

    const data = await response.json();
    const rawInvoices: Invoice[] = data.invoices ?? [];
    // L'edge function renvoie createdAt en string ISO ; le type Invoice
    // attend un Date (utilisé par l'UI via .toISOString()).
    return rawInvoices.map((inv) => ({
      ...inv,
      createdAt:
        inv.createdAt instanceof Date
          ? inv.createdAt
          : new Date(inv.createdAt as unknown as string),
    }));
  }

  /**
   * Change de plan - redirige vers checkout
   */
  async changePlan(planId: string): Promise<void> {
    try {
      // Récupérer la session (on a besoin de user ET du access_token pour l'Edge Function)
      const {
        data: { session },
        error: authError,
      } = await this.supabase.auth.getSession();

      if (authError || !session || !session.user) {
        throw new Error("User not authenticated");
      }

      const variantId = this.getVariantId(planId);

      const response = await fetch(`${this.baseUrl}/create-checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          variantId,
          returnPath: "/account/subscription",
        }),
      });

      if (!response.ok) {
        const body = await response.text().catch(() => "");
        throw new Error(
          `Failed to create checkout: ${response.status} ${response.statusText}${body ? ` — ${body}` : ""}`,
        );
      }

      const data = await response.json();

      if (!data?.url) {
        throw new Error("Checkout response missing URL");
      }

      // Rediriger vers la page de checkout
      window.location.href = data.url;
    } catch (error) {
      console.error("Error creating checkout:", error);
      throw error;
    }
  }

  /**
   * Annule le renouvellement via la Edge Function manage-subscription
   */
  async cancelRenewal(): Promise<void> {
    await this.manageSubscription("cancel");
  }

  /**
   * Réactive le renouvellement via la Edge Function manage-subscription
   */
  async resumeRenewal(): Promise<void> {
    await this.manageSubscription("resume");
  }

  /**
   * Appelle manage-subscription avec l'action donnée
   */
  private async manageSubscription(action: "cancel" | "resume"): Promise<void> {
    const {
      data: { session, user },
      error: authError,
    } = await this.supabase.auth.getSession();

    if (authError || !session || !user) {
      throw new Error("User not authenticated");
    }

    // L'abonnement visé est celui du PRODUIT COURANT. `profiles` ne porte
    // qu'un seul identifiant pour tous les produits — celui du dernier achat —
    // si bien qu'un client abonné à plusieurs produits y annulait le mauvais,
    // ou se voyait refuser l'opération par l'edge function.
    const { data: sub, error: subError } = await this.supabase
      .from("product_subscriptions")
      .select("lemonsqueezy_subscription_id")
      .eq("user_id", user.id)
      .eq("product", getSupportProduct())
      .maybeSingle();

    if (subError || !sub?.lemonsqueezy_subscription_id) {
      throw new Error("No active subscription found");
    }

    const response = await fetch(`${this.baseUrl}/manage-subscription`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({
        action,
        subscriptionId: sub.lemonsqueezy_subscription_id,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error ?? `Failed to ${action} subscription`);
    }
  }

  /**
   * Met à jour la méthode de paiement
   */
  async updatePaymentMethod(paymentMethodId: string): Promise<void> {
    try {
      // Récupérer la session (access_token requis : l'Edge Function dérive
      // désormais userId/email du JWT, cf. fix #780).
      const {
        data: { session },
        error: authError,
      } = await this.supabase.auth.getSession();

      if (authError || !session || !session.user) {
        throw new Error("User not authenticated");
      }

      // Pour la mise à jour de méthode de paiement, on utilise le variant SOLO par défaut
      const variantId = this.getVariantId("SOLO");

      const response = await fetch(`${this.baseUrl}/create-checkout`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          variantId,
          returnPath: "/account/subscription",
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to create payment method update: ${response.statusText}`,
        );
      }

      const data = await response.json();

      // Rediriger vers la page de checkout pour mise à jour
      window.location.href = data.url;
    } catch (error) {
      console.error("Error updating payment method:", error);
      throw error;
    }
  }

  /**
   * Crée un SubscriptionPlan à partir du plan type (basé sur MockBillingService)
   */
  private createSubscriptionPlan(planType: string): SubscriptionPlan {
    const plans: Record<string, SubscriptionPlan> = {
      FREE: {
        id: "FREE",
        name: "DooDates Gratuit",
        amount: 0,
        currency: "EUR",
        period: "lifetime",
        features: [
          "3 conversations IA à vie",
          "5 messages maximum par conversation",
          "Création manuelle de sondages illimitée",
          "Participants illimités",
          "Dashboard complet",
          "Notifications par email",
          "Modifications après envoi",
          "Exports CSV, JSON, Markdown",
        ],
      },
      SOLO: {
        id: "SOLO",
        name: "DooDates Solo",
        amount: 3000, // 30EUR en cents
        currency: "EUR",
        period: "yearly",
        features: [
          "IA illimitée",
          "10 messages maximum par conversation",
          "Création de sondages illimitée",
          "Participants illimités",
          "Dashboard complet",
          "Notifications par email",
          "Modifications après envoi",
          "Exports CSV, Excel, PDF, JSON, Markdown",
          "Intégration Google Calendar",
          "Fonctions IA en avant-première",
        ],
      },
    };

    return plans[planType] || plans.FREE;
  }

  /**
   * Convertit plan type en variant ID LemonSqueezy
   */
  private getVariantId(planType: string): string {
    const variants: Record<string, string> = {
      FREE: "variant-free", // Non utilisé dans LemonSqueezy
      SOLO: "1513652", // Vrai variant ID de DooDates Solo (mis à jour)
    };

    return variants[planType] || "963876";
  }
}
